import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../context/DataContext';
import { 
  ChevronRight, 
  Factory, 
  Sparkles, 
  Box
} from 'lucide-react';
import Translate from '../components/Translate';

import { useTranslation } from 'react-i18next';
import { getLangText } from '../utils/langHelper';

const Products = () => {
  const [filter, setFilter] = useState('All');
  const { data, loading } = useData();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const products = [...(data.products || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const categories = ['All', 'Food Products', 'Industrial & Agricultural Products'];

  const filteredProducts = filter === 'All' 
    ? products 
    : (products || []).filter(p => {
        const cat = (p?.category || '').toLowerCase();
        if (filter === 'Food Products') {
          return cat === 'food products' || cat === 'sugar' || cat === 'jaggery';
        }
        if (filter === 'Industrial & Agricultural Products') {
          return cat === 'industrial & agricultural products' || cat === 'other';
        }
        return cat === filter.toLowerCase();
      });

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-ivory">
        <div className="w-10 h-10 border-4 border-emerald border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory pb-24 font-poppins">
      <PageHeader 
        title={<Translate>Our Premium Products</Translate>} 
        subtitle={<Translate>Purity You Can Trust</Translate>} 
      />

      {/* Filter Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
        
        {/* Toggle Categories Switcher */}
        <div className="flex justify-center mb-14">
          <div className="inline-flex bg-white p-2 rounded-2xl border border-gray-100 shadow-premium gap-1 overflow-x-auto max-w-full">
            {categories.map((cat) => {
              const isActive = filter === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-7 py-3 rounded-xl text-xs font-heading font-extrabold tracking-wider uppercase transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center space-x-2 ${
                    isActive
                      ? 'bg-emerald text-white shadow-emerald'
                      : 'text-slate hover:text-emerald hover:bg-gray-50'
                  }`}
                >
                  {cat === 'Industrial & Agricultural Products' && <Factory size={14} className="text-gold" />}
                  {cat === 'Food Products' && <Sparkles size={14} />}
                  <span><Translate>{cat}</Translate></span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Smooth Animated Grid Container */}
        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
          >
            {filteredProducts.length === 0 ? (
              <div className="py-20 text-center bg-white rounded-3xl border border-gray-100 shadow-premium max-w-md mx-auto">
                <div className="w-12 h-12 bg-emerald/10 text-emerald rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Box size={24} />
                </div>
                <h4 className="text-lg font-heading font-bold text-charcoal mb-2"><Translate>No Products Available</Translate></h4>
                <p className="text-xs text-slate px-6"><Translate>There are currently no products published in this category.</Translate></p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProducts.map((product, idx) => {
                  const pCat = (product.category || '').toLowerCase();
                  const isFood = pCat.includes('food') || pCat === 'sugar' || pCat === 'jaggery';

                  return (
                    <motion.div
                      key={product.id || idx}
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: idx * 0.05 }}
                      className={`premium-card overflow-hidden group flex flex-col h-full bg-white rounded-2xl border transition-all duration-300 ${
                        !isFood 
                          ? 'border-emerald/20 hover:border-emerald/50 shadow-md hover:shadow-xl' 
                          : 'border-gray-100 shadow-premium hover:shadow-premium-xl'
                      }`}
                    >
                      {/* Card Image Container */}
                      <Link to={`/products/${product.id}`} className="relative h-64 overflow-hidden flex-shrink-0 bg-mist block">
                        <img
                          src={product.image || 'https://via.placeholder.com/600'}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        />
                        
                        {/* Category Badges */}
                        <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5 z-10">
                          <span className={`px-3 py-1 rounded-lg text-[10px] font-heading font-extrabold uppercase tracking-wider shadow-sm border backdrop-blur-md ${
                            !isFood
                              ? 'bg-forest/90 text-gold border-gold/30'
                              : 'bg-white/95 text-emerald border-emerald/10'
                          }`}>
                            <Translate>{isFood ? 'Food Products' : 'Industrial & Agricultural'}</Translate>
                          </span>
                        </div>
                      </Link>

                      {/* Card Main Info */}
                      <div className="flex flex-col flex-grow p-7">
                        <div className="flex justify-between items-start mb-2">
                          <Link to={`/products/${product.id}`}>
                            <h3 className="text-xl font-heading font-bold text-charcoal tracking-tight group-hover:text-emerald transition-colors duration-300">
                              <Translate>{getLangText(product, 'name', lang)}</Translate>
                            </h3>
                          </Link>
                        </div>

                        <p className="text-slate text-xs leading-relaxed mb-6 line-clamp-3 flex-grow">
                          <Translate>{getLangText(product, 'desc', lang)}</Translate>
                        </p>

                        {/* Footer Action */}
                        <div className="flex items-center justify-end pt-5 mt-auto border-t border-gray-100">
                          {/* Direct link to full detail page */}
                          <Link
                            to={`/products/${product.id}`}
                            className="flex items-center space-x-1.5 text-emerald font-heading font-bold text-xs group-hover:text-forest transition-colors duration-300 cursor-pointer"
                          >
                            <span><Translate>View Details</Translate></span>
                            <ChevronRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Products;
